import { Injectable, Injector, NgZone } from '@angular/core';

import { environment } from '../../../../../environments/environment';
import { BUILD_INFO } from '../../../../../environments/build-info';
import { OpenPanelService } from '../../openPanel.service';
import { RestaurantService } from '../../restaurant.service';
import { OrderFlowSessionService } from '../order-flow-session.service';
import { CLIENT_CODE_VERSION } from '../order-flow-trace.service';
import { lerDoNotTrack, slugDoPath } from '../gate-utils';
import {
  DecisaoCarga, MotivoBloqueio, OpenReplayConfig, OpenReplayGateContext,
  decidirAgendamento, decidirCarga,
} from './openreplay-config';
import type { ContextoOpenReplay, OpenReplayRuntime, ResultadoInicio } from './openreplay-runtime';

/**
 * OpenReplayService — orquestra o ciclo de vida do tracker sem nunca importá-lo.
 *
 * Divisão de trabalho, espelhando o Sentry (SENTRY.md §1):
 *   - `openreplay-config`   decide SE este device grava (gates puros);
 *   - `openreplay-privacy`  decide O QUE não pode ser gravado;
 *   - este serviço          decide QUANDO carrega e liga uma coisa na outra;
 *   - `openreplay-runtime`  chunk lazy, o único que fala com o SDK.
 *
 * ## "Quando", e as duas diferenças em relação ao Sentry
 *
 * Igual ao Sentry: **idle depois do `load`**. Carregar no bootstrap devolveria
 * ao caminho crítico os ~54 KB que o épico de performance existe para não pagar.
 *
 * **Diferença 1 — não há antecipação por erro.** No Sentry, um erro chegando à
 * fila puxa a carga para a frente, porque uma página que já quebrou não tem mais
 * orçamento de performance a proteger e o erro AINDA PODE ser enviado. Aqui não
 * adiantaria: a gravação começaria depois do erro, e o que interessa num replay
 * é justamente o que veio antes. Antecipar custaria performance sem comprar
 * nada.
 *
 * **Diferença 2 — o gate roda duas vezes.** No bootstrap a loja ainda não
 * existe (quem a resolve é outro `APP_INITIALIZER`, que bloqueia o bootstrap);
 * no `load` ela existe. Então o agendamento usa os gates baratos e a carga
 * reavalia tudo, incluindo a loja. É essa segunda avaliação que a flag por loja
 * vai usar quando o backend a tiver — ver `lojaNoEscopo()`.
 */

/** Estados observáveis — o spec e a sonda `window.__mpOpenReplay` afirmam por aqui. */
export type EstadoOpenReplay = 'inativo' | 'bloqueado' | 'agendado' | 'carregando' | 'ativo' | 'falhou';

/** Folga após o `load` antes de tentar o idle. Alinhado ao Sentry e ao index.html. */
const ATRASO_POS_LOAD_MS = 3000;
/** Teto do requestIdleCallback: sem isto, uma aba de fundo nunca carregaria. */
const IDLE_TIMEOUT_MS = 8000;

@Injectable({ providedIn: 'root' })
export class OpenReplayService {
  private estado: EstadoOpenReplay = 'inativo';
  private motivo: MotivoBloqueio = '';
  private runtime: OpenReplayRuntime | null = null;
  private agendado = false;
  private sessionId = '';
  /**
   * Desfecho do `start()` do SDK, que é ASSÍNCRONO e independente do nosso
   * gate: `pendente` até ele resolver, depois `ok` ou o motivo que o próprio
   * SDK deu. Sem este campo, `{estado: 'ativo', sessionId: ''}` é ambíguo —
   * pode ser "ainda não resolveu" ou "resolveu e recusou", e as duas coisas
   * pedem investigações opostas.
   */
  private inicio: string = '';

  constructor(
    private session: OrderFlowSessionService,
    private zone: NgZone,
    private injector: Injector,
  ) { }

  /**
   * Ponto de entrada (APP_INITIALIZER). Síncrono e barato de propósito: só
   * decide e agenda — não retorna promise, para não somar um milissegundo
   * sequer ao bootstrap.
   */
  boot(): void {
    try {
      const decisao = this.decidirAgora(decidirAgendamento);
      this.motivo = decisao.motivo;

      if (!decisao.carregar) {
        this.estado = 'bloqueado';
        // O histórico de eventos do funil NÃO é descartado aqui: o gate é
        // deste gravador, e o outro carrega por conta própria. Descartar
        // cegaria quem não tem nada a ver com esta decisão. O teto do próprio
        // histórico é que limita a memória.
        this.expor();
        return;
      }

      this.estado = 'agendado';
      this.agendarCarga();
      this.expor();
    } catch (e) {
      // Falha ao decidir nunca derruba o boot do app.
      this.estado = 'falhou';
      this.expor();
    }
  }

  /** Estado atual — usado pelos testes e pela sonda `window.__mpOpenReplay`. */
  get estadoAtual(): EstadoOpenReplay { return this.estado; }
  get motivoBloqueio(): MotivoBloqueio { return this.motivo; }
  get sessaoAtual(): string { return this.sessionId; }

  /**
   * Config e ambiente saem por métodos `protected` — e não lidos inline — pelo
   * mesmo motivo que no SentryService: são o ponto de substituição do teste. Sem
   * isso, todo spec veria apenas o `environment` de desenvolvimento (chave
   * vazia) e a matriz de gates ficaria sem cobertura — justamente a parte que,
   * se errar, grava quem não devia.
   */
  protected config(): OpenReplayConfig | undefined {
    const base = (environment as any).openReplay as OpenReplayConfig | undefined;
    // Override por query param — SÓ no ambiente local (`permitirLocalhost`),
    // para variar as alavancas do tracker sem rebuild ao investigar. Ex.:
    // `?orcfg=nodesMaintainer:0,inlineCss:1,forcarNovaSessao:1`. Nunca faz nada
    // fora do local: produção não liga `permitirLocalhost`, e o parse só roda
    // sob essa flag.
    if (base && (base as any).permitirLocalhost) {
      try {
        const q = new URLSearchParams(window.location.search).get('orcfg');
        if (q) {
          const over: any = { ...base };
          for (const par of q.split(',')) {
            const [k, v] = par.split(':');
            if (!k) continue;
            const chave = k.trim();
            const val = (v || '').trim();
            if (chave === 'inlineCss') over.inlineCss = Number(val);
            else if (chave === 'clientSamplePct') over.clientSamplePct = Number(val);
            else if (['nodesMaintainer', 'domSanitizerAtivo', 'forcarNovaSessao',
                      'obscureTextNumbers'].indexOf(chave) !== -1) over[chave] = val === '1' || val === 'true';
          }
          (window as any).__mpOrCfg = over;
          return over as OpenReplayConfig;
        }
      } catch (e) { /* query malformada não pode derrubar a decisão */ }
    }
    return base;
  }

  protected ambiente(): OpenReplayGateContext {
    const win: any = typeof window !== 'undefined' ? window : {};
    const nav: any = typeof navigator !== 'undefined' ? navigator : {};
    const info = this.infoDaLoja();
    return {
      production: !!environment.production,
      hostname: (win.location && win.location.hostname) || '',
      isCypress: !!win.Cypress,
      doNotTrack: lerDoNotTrack(nav, win),
      deviceId: this.deviceIdSeguro(),
      restaurantId: info ? (info.id ?? null) : null,
      restaurantModules: info ? (info.modules ?? null) : null,
    };
  }

  /**
   * `info` da loja, ou `null`.
   *
   * `null` não é só "ainda não carregou": é também loja inexistente e prazo do
   * cardápio estourado (`falhaDeCarga === 'prazo'`), e nesses casos
   * `restaurantService.restaurant` fica `undefined`. Ler sem guarda aqui
   * repetiria o `TypeError` que já custou um issue no `app.component.ts`.
   *
   * O `RestaurantService` sai do `Injector` sob demanda, e não do construtor,
   * porque este serviço nasce num `APP_INITIALIZER` — pedi-lo cedo criaria uma
   * dependência de boot que não precisa existir.
   */
  private infoDaLoja(): any | null {
    try {
      const svc = this.injector.get(RestaurantService, null);
      return (svc && (svc as any).restaurant && (svc as any).restaurant.info) || null;
    } catch (e) {
      return null;
    }
  }

  private decidirAgora(
    regra: (cfg: OpenReplayConfig | undefined, ctx: OpenReplayGateContext) => DecisaoCarga,
  ): DecisaoCarga {
    return regra(this.config(), this.ambiente());
  }

  /**
   * `deviceId` sem deixar o gate depender de storage. Em modo privado/iframe a
   * leitura pode lançar, e id vazio é tratado pelos gates como fora da amostra —
   * sem id estável não há coorte estável.
   */
  private deviceIdSeguro(): string {
    try {
      return this.session.deviceId || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Agenda a carga para o primeiro idle após o `load` + folga.
   *
   * `runOutsideAngular` em tudo: um callback de idle dentro da zone dispara um
   * ciclo de change detection do app inteiro sem nenhuma mudança de estado —
   * anti-padrão explícito neste repo.
   */
  private agendarCarga(): void {
    this.zone.runOutsideAngular(() => {
      const depois = () => setTimeout(() => this.emIdle(() => this.carregar()), ATRASO_POS_LOAD_MS);
      try {
        if (typeof document !== 'undefined' && document.readyState === 'complete') {
          depois();
        } else if (typeof window !== 'undefined') {
          window.addEventListener('load', depois, { once: true } as any);
        } else {
          depois();
        }
      } catch (e) {
        depois();
      }
    });
  }

  /** `protected` para o spec poder tornar o agendamento determinístico: o
   *  `jasmine.clock()` não controla `requestIdleCallback`. */
  protected emIdle(fn: () => void): void {
    const w: any = typeof window !== 'undefined' ? window : null;
    try {
      if (w && typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(fn, { timeout: IDLE_TIMEOUT_MS });
        return;
      }
    } catch (e) { /* cai no setTimeout */ }
    setTimeout(fn, 0);
  }

  /**
   * O `import()` dinâmico — a fronteira do chunk.
   *
   * Antes dele, a decisão COMPLETA: é aqui, e só aqui, que o gate de loja roda,
   * porque é o primeiro instante em que a loja existe. Um `bloqueado` acontecendo
   * neste ponto é normal e fica visível na sonda.
   */
  protected carregar(): void {
    if (this.agendado) return;
    this.agendado = true;

    const decisao = this.decidirAgora(decidirCarga);
    if (!decisao.carregar) {
      this.estado = 'bloqueado';
      this.motivo = decisao.motivo;
      this.expor();
      return;
    }

    this.estado = 'carregando';
    this.expor();

    import('./openreplay-runtime')
      .then((mod) => {
        this.runtime = mod.initOpenReplayRuntime({
          // `this.config()`, e não o environment lido de novo: um segundo ponto
          // de acesso ao formato do environment sairia do alcance do teste.
          cfg: this.config() as OpenReplayConfig,
          contexto: this.contextoAtual(),
          aoIniciar: (r: ResultadoInicio) => {
            this.sessionId = r.sessionId;
            this.inicio = r.ok ? 'ok' : r.motivo;
            // Ponte INVERSA: com a URL da gravação como propriedade global do
            // OpenPanel, uma linha do funil vira um clique para o replay. Sem
            // isto a integração é de mão única.
            if (r.ok) this.publicarUrlNoOpenPanel(r.sessionUrl);
            // O SDK pode recusar o start por conta própria. Sem isto a sonda
            // diria `ativo` para uma sessão que nunca existiu — e "está
            // gravando e não aparece no painel" é a pior pergunta possível.
            if (!r.ok) this.estado = 'falhou';
            this.expor();
          },
        });
        // `pendente`: o chunk chegou e o Tracker foi construído, mas o `start()`
        // ainda não resolveu. É um estado real e observável, não um limbo.
        if (this.runtime && !this.inicio) this.inicio = 'pendente';
        this.estado = this.runtime ? 'ativo' : 'falhou';
        this.expor();
      })
      .catch(() => {
        // Chunk não carregou (rede, deploy no meio da sessão). Erro de
        // telemetria não vira erro de app.
        this.estado = 'falhou';
        this.expor();
      });
  }

  /**
   * `OpenPanelService` pelo Injector, e não pelo construtor, pela mesma razão do
   * `RestaurantService`: este serviço nasce num APP_INITIALIZER, e a ponte
   * inversa só interessa depois que a sessão abriu.
   */
  private publicarUrlNoOpenPanel(url: string): void {
    if (!url) return;
    try {
      const op = this.injector.get(OpenPanelService, null);
      if (op) op.setGlobalProperty('replay_url', url);
    } catch (e) { /* a ponte é conveniência; a gravação já está de pé */ }
  }

  private contextoAtual(): ContextoOpenReplay {
    let restaurant = '';
    try {
      restaurant = slugDoPath(window.location.pathname);
    } catch (e) { /* fica vazio */ }
    const info = this.infoDaLoja();
    return {
      deviceId: this.deviceIdSeguro(),
      attemptId: this.seguro(() => this.session.attemptId),
      restaurant,
      restaurantId: info ? (info.id ?? null) : null,
      clientCodeVersion: CLIENT_CODE_VERSION,
      addressFlow: this.seguro(() => this.session.addressFlow),
      release: BUILD_INFO.release,
    };
  }

  private seguro(fn: () => string): string {
    try {
      return fn() || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Sonda de diagnóstico. Sem ela, "não estou vendo sessão nenhuma" vira uma
   * investigação; com ela, é `__mpOpenReplay` no console e a resposta sai em um
   * segundo (`{estado: 'bloqueado', motivo: 'fora-da-amostra'}`).
   */
  private expor(): void {
    try {
      (window as any).__mpOpenReplay = {
        estado: this.estado,
        motivo: this.motivo,
        inicio: this.inicio,
        sessionId: this.sessionId,
      };
    } catch (e) { /* ignorado */ }
  }
}
